import { useContext, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import copy from "copy-to-clipboard";
import { PhoneInput } from "react-international-phone";
import AuthContext from "../context/auth/AuthContext";
import LandLordContext from "../context/landlord/LandLordContext";
import useTranslator from "../hooks/useTranslator";
import { toast, validatePhoneNumber } from "../helper/common";
import {
  Badge,
  Button,
  Control,
  Field,
  Icon,
  Modal,
  TextField,
} from "./design";
import "./InviteTenantModal.css";

const EMAIL_PATTERN = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

/**
 * "Invite tenant": QR code and link for the current workspace, then
 * optionally a step to send the invitation by email or WhatsApp.
 * Used by the Tenants page and the top bar (phone menu).
 */
const InviteTenantModal = ({ open, onClose }) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams();
  const { user } = useContext(AuthContext);
  const { sendInvitationToTenant } = useContext(LandLordContext);

  // "qr" | "email" | "phone"
  const [step, setStep] = useState("qr");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  const workspace = user?.current_workspace;
  const inviteLink =
    user?.role === "landlord" &&
    workspace?.customLinkSlug &&
    workspace?.customLinkEnabled === true
      ? `${window.location.protocol}//${window.location.host}/oauth/${workspace.customLinkSlug}`
      : `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/home?requestProfileLink=${user?._id}_${workspaceId}`;

  const close = () => {
    setStep("qr");
    setName("");
    setEmail("");
    setPhone("");
    onClose();
  };

  const copyLink = () => {
    copy(inviteLink);
    toast.success(_t("linkCopied"));
  };

  const openInvitationSettings = () => {
    close();
    const params = new URLSearchParams(location.search);
    params.set("openEditableTab", "invitations");
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const send = async (e) => {
    e.preventDefault();
    if (step === "email" && !EMAIL_PATTERN.test(email)) {
      toast.error(_t("invalidEmailAddress"));
      return;
    }
    if (step === "phone") {
      const validate = validatePhoneNumber(phone);
      if (!validate.isValid) {
        toast.error(validate.message);
        return;
      }
    }
    setSending(true);
    try {
      const response = await sendInvitationToTenant(
        step === "email"
          ? { type: "email", name, email }
          : { type: "phone", name, phone }
      );
      if (response.data?.message) toast.success(response.data.message);
      close();
    } catch (error) {
      if (error.response?.data?.err) toast.error(error.response.data.err);
    } finally {
      setSending(false);
    }
  };

  if (step !== "qr") {
    return (
      <Modal
        open={open}
        onClose={close}
        size="sm"
        align="start"
        title={_t(
          step === "email" ? "shareViaEmail" : "shareViaPhone",
          "Landlord side in tenant profile section"
        )}
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              disabled={sending}
              onClick={() => setStep("qr")}
            >
              {_t("back")}
            </Button>
            <Button
              type="submit"
              form="invite-tenant-form"
              size="lg"
              loading={sending}
            >
              {_t("sendInvitation")}
            </Button>
          </>
        }
      >
        <form
          id="invite-tenant-form"
          className="invite-tenant-modal__form"
          onSubmit={send}
          noValidate
        >
          <TextField
            id="invite-tenant-name"
            label={_t("name")}
            placeholder={_t("name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBeforeInput={(e) => {
              if (e.data && !/^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data))
                e.preventDefault();
            }}
            required
          />
          {step === "email" ? (
            <TextField
              id="invite-tenant-email"
              type="email"
              label={_t("email")}
              placeholder={_t("email")}
              icon={<Icon name="mail" />}
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              required
            />
          ) : (
            <Field label={_t("phone")}>
              <Control>
                <PhoneInput
                  international
                  defaultCountry={"es"}
                  value={phone}
                  onChange={(value) => setPhone(value?.replace("+", "") || "")}
                />
              </Control>
            </Field>
          )}
        </form>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title={_t("yourProfileQR")}
      description={
        <>
          {_t("shareThisLinkSoThatTenantProfileWillBeLinkedTo")}
          <span className="invite-tenant-modal__workspace">
            <Badge>
              <Icon name="home" size={14} />
              {workspace?.name}
            </Badge>
            {_t("workspaceAutomatically")}
          </span>
        </>
      }
      actionsLayout="stack"
      actions={
        <>
          <Button
            size="lg"
            iconStart={<Icon name="mail" />}
            onClick={() => setStep("email")}
          >
            {_t("shareViaEmail", "Landlord side in tenant profile section")}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            iconStart={<Icon name="phone" />}
            onClick={() => setStep("phone")}
          >
            {_t("shareViaPhone", "Landlord side in tenant profile section")}
          </Button>
        </>
      }
    >
      <div className="invite-tenant-modal__qr">
        <QRCode id="qr_code_image" value={inviteLink} size={168} />
      </div>

      <div className="invite-tenant-modal__link">
        <TextField
          id="invite-tenant-link"
          aria-label={_t("copyURL")}
          value={inviteLink}
          readOnly
          onFocus={(e) => e.target.select()}
        />
        <Button
          variant="secondary"
          size="lg"
          iconStart={<Icon name="copy" />}
          onClick={copyLink}
        >
          {_t("copyURL")}
        </Button>
      </div>

      {workspace?.customLinkEnabled && (
        <div className="invite-tenant-modal__settings">
          <Button
            variant="ghost"
            size="sm"
            iconStart={<Icon name="edit" size={16} />}
            onClick={openInvitationSettings}
          >
            {_t("editInvitationSettings")}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default InviteTenantModal;
